import React, { useState, useEffect } from 'react';

// project-imports
import { useEnquiryContext } from '../../context/enquiry_context';
import { GoogleAnalyticsEnum, triggerFunction } from '../../utils/google-analytics';
import { isBlockedDomain } from '../../utils';

// Reading environment variables
const ALL_FIELDS_ERROR_MESSAGE = process.env.REACT_APP_ALL_FIELD_ERROR_MESSAGE;
const INVALID_NAME_ERROR_MESSAGE = process.env.REACT_APP_INVALID_FULLNAME_ERROR_MESSAGE;
const INVALID_EMAIL_ERROR_MESSAGE = process.env.REACT_APP_INVALID_EMAIL_ERROR_MESSAGE;
const NAME_ERROR_MESSAGE = process.env.REACT_APP_NAME_ERROR_MESSAGE;
const EMAIL_ERROR_MESSAGE = process.env.REACT_APP_EMAIL_ERROR_MESSAGE;
const PHONE_ERROR_MESSAGE = process.env.REACT_APP_PHONE_ERROR_MESSAGE;
const SUBJECT_ERROR_MESSAGE = process.env.REACT_APP_SUBJECT_ERROR_MESSAGE;
const MESSAGE_ERROR_MESSAGE = process.env.REACT_APP_MESSAGE_ERROR_MESSAGE;

// Success Message Component
const Result = () => (
    <p className="success-message">Thanks for your query. We will contact with you soon.</p>
);

function ContactUsForm({ formStyle }) {

    // Reading Context methods and variables
    const { submitEnquiry, isLoading } = useEnquiryContext();

    // Local stat initialization
    const [result, setResult] = useState(false);
    const [enquiryForm, setEnquiryForm] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [inputValidationErrors, setInputValidationErrors] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
    });

    // Constant variable
    const patterns = {
        name: /^[A-Za-z\s]{3,20}$/,
        email: /^[a-zA-Z0-9!#$%&*+=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&*=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9]{2,}(?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/,
        phone: /^[6-9]\d{9}$/,
        subject: /^[A-Za-z0-9\s]{3,50}$/,
        message: /^[\w\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{3,500}$/
    };

    // Constant functions
    // Validation handler
    const validateField = (data, name) => {
        try {
            // const isValid = patterns[name].test(data[name]?.value);
            let validationObj = {};
            if (name === "name" || name === "all") {
                if (data && (!data.name || (data.name && !data.name.length))) {
                    validationObj.name = "Name is required!"
                } else if (data && data.name && !(patterns["name"].test(data?.name))) {
                    validationObj.name = NAME_ERROR_MESSAGE;
                } else if (data && data.name && isBlockedDomain(data?.name)) {
                    validationObj.name = INVALID_NAME_ERROR_MESSAGE;
                } else {
                    validationObj.name = "";
                }
            }
            if (name === "email" || name === "all") {
                if (data && (!data.email || (data.email && !data.email.length))) {
                    validationObj.email = "Email is required!"
                } else if (data && data.email && !(patterns["email"].test(data?.email))) {
                    validationObj.email = EMAIL_ERROR_MESSAGE;
                } else if (data && data.email && isBlockedDomain(data?.email)) {
                    validationObj.email = INVALID_EMAIL_ERROR_MESSAGE;
                } else {
                    validationObj.email = "";
                }
            }
            if (name === "phone" || name === "all") {
                if (data && (!data.phone || (data.phone && !data.phone.length))) {
                    validationObj.phone = "Phone No. is required!"
                } else if (data && data.phone && !(patterns["phone"].test(data?.phone))) {
                    validationObj.phone = PHONE_ERROR_MESSAGE;
                } else {
                    validationObj.phone = "";
                }
            }
            if (name === "subject" || name === "all") {
                if (data && (!data.subject || (data.subject && !data.subject.length))) {
                    validationObj.subject = "Subject is required!"
                } else if (data && data.subject && !(patterns["subject"].test(data?.subject))) {
                    validationObj.subject = SUBJECT_ERROR_MESSAGE;
                } else {
                    validationObj.subject = "";
                }
            }
            if (name === "message" || name === "all") {
                if (data && (!data.message || (data.message && !data.message.length))) {
                    validationObj.message = "Message is required!"
                } else if (data && data.message && !(patterns["message"].test(data?.message))) {
                    validationObj.message = MESSAGE_ERROR_MESSAGE;
                } else {
                    validationObj.message = "";
                }
            }
            setInputValidationErrors(validationObj);
            return validationObj;
        } catch (error) {
            console.log("105 : ContactUsForm : Error while checking validation :", error && JSON.stringify(error));
            throw error;
        }
    };

    const handleChange = (e) => {
        try {
            const { name, value } = e.target;

            const data = {
                [name]: value
            };

            setInputValidationErrors((prev) => ({ ...prev, ...validateField(data, name) }));
            setEnquiryForm((prev) => ({ ...prev, ...data }));
        } catch (error) {
            console.log("121 : ContactUsForm : Error while changing value :", error && JSON.stringify(error));
            throw error;
        }
    };

    const handleSubmitEnquiry = () => {
        try {

            let isFormValid = true;
            const valid = validateField(enquiryForm, "all");

            if (valid.name || valid.email || valid.phone || valid.subject || valid.message) {
                isFormValid = false;
            }

            if (isFormValid) {

                const isAnyFieldEmpty = Object.values(enquiryForm).some((val) => !val.trim());
                const hasValidationErrors = Object.values(inputValidationErrors).some((error) => error);

                if (isAnyFieldEmpty || hasValidationErrors) {
                    return;
                }

                triggerFunction(GoogleAnalyticsEnum.BUTTON, GoogleAnalyticsEnum.CONTACT_US_FORM_SUBMIT_BUTTON);
                submitEnquiry(enquiryForm);

                // e.target.reset();
                setEnquiryForm({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                });
                setResult(true);
            }
        } catch (error) {
            console.log("159 : ContactUsForm : Error while submitting message :", error && JSON.stringify(error));
            throw error;
        }
    };

    // Reset result message after 2s
    useEffect(() => {
        if (result) {
            const timer = setTimeout(() => setResult(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [result]);

    return (
        <div className={formStyle}>
            <div className="col-lg-12">
                <div className="form-group">
                    <input
                        disabled={isLoading}
                        type="text"
                        value={enquiryForm?.name || ""}
                        name="name"
                        placeholder="Name*"
                        className="form-control form-control-lg"
                        onChange={handleChange}
                        autoFocus
                    />
                    {inputValidationErrors && inputValidationErrors.name && (
                        <div className="invalid-data">{inputValidationErrors.name}</div>
                    )}
                </div>
            </div>

            <div className="col-lg-12">
                <div className="form-group">
                    <input
                        disabled={isLoading}
                        type="email"
                        name="email"
                        value={enquiryForm?.email || ""}
                        placeholder="Email*"
                        className="form-control form-control-lg"
                        onChange={handleChange}
                        title="Please enter a valid email"
                    />
                    {inputValidationErrors && inputValidationErrors.email && (
                        <div className="invalid-data">{inputValidationErrors.email}</div>
                    )}
                </div>
            </div>

            <div className="col-lg-12">
                <div className="form-group">
                    <input
                        disabled={isLoading}
                        type="text"
                        name="phone"
                        value={enquiryForm?.phone || ""}
                        placeholder="Phone"
                        className="form-control form-control-lg"
                        onChange={handleChange}
                    />
                    {inputValidationErrors && inputValidationErrors.phone && (
                        <div className="invalid-data">{inputValidationErrors.phone}</div>
                    )}
                </div>
            </div>

            <div className="col-lg-12">
                <div className="form-group">
                    <input
                        disabled={isLoading}
                        type="text"
                        name="subject"
                        value={enquiryForm?.subject || ""}
                        placeholder="Subject"
                        className="form-control form-control-lg"
                        onChange={handleChange}
                    />
                    {inputValidationErrors && inputValidationErrors.subject && (
                        <div className="invalid-data">{inputValidationErrors.subject}</div>
                    )}
                </div>
            </div>

            <div className="col-lg-12">
                <div className="form-group">
                    <textarea
                        disabled={isLoading}
                        name="message"
                        value={enquiryForm?.message || ""}
                        placeholder="Your Message"
                        className="form-control"
                        onChange={handleChange}
                    />
                    {inputValidationErrors && inputValidationErrors.message && (
                        <div className="invalid-data">{inputValidationErrors.message}</div>
                    )}
                </div>
            </div>

            {/* <div className="col-lg-12">
                <div className="form-group">
                    {formSubmitted && Object.values(enquiryForm).some(val => !val.trim()) && (
                        <div className="invalid-data">{ALL_FIELDS_ERROR_MESSAGE}</div>
                    )}
                </div>
            </div> */}

            <div className="col-lg-12">
                <div className="form-group">
                    <button
                        disabled={isLoading}
                        className={`rn-btn edu-btn w-100 ${isLoading ? 'btn-loading' : ''}`}
                        type="button"
                        onClick={handleSubmitEnquiry}
                    >
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Loading...
                            </>
                        ) : (
                            <>
                                <span>Submit Now</span>
                                <i className="icon-arrow-right-line-right" />
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* {result && (
                <div className="form-group">
                    <Result />
                </div>
            )} */}
        </div>
    );
}

export default ContactUsForm;
